import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, ChevronUp, X } from "lucide-react";
import { trackEvent } from "@/lib/posthog";
import { fireConfettiCannons } from "@/lib/confetti";

/**
 * LevelUpModal — Iter70 / Phase 3.
 *
 * Globally-mounted celebratory modal. Listens for the `oak:level-up`
 * event (fired by `XPBadge` when it detects a level number increase
 * between snapshots) and pops a confetti-style overlay.
 *
 * Why a separate event instead of every endpoint dispatching directly?
 * The XP-awarding endpoints (`/games/complete`, `/lesson/quiz/grade`,
 * `/streak/ping`, `/counselor/path`) ALL return an `xp` payload with
 * `leveled_up: boolean`. But we don't want each call-site re-implementing
 * "watch for leveled_up and pop the modal". Instead callers just fire
 * `oak:xp-updated` with the snapshot; the XPBadge owns level-tracking
 * and fires `oak:level-up` exactly once. One source of truth.
 *
 * Confetti pieces are pure CSS keyframes — no extra dependency.
 */
export default function LevelUpModal() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const onLevelUp = (e) => {
      const d = e?.detail;
      if (!d || typeof d.level !== "number") return;
      setInfo(d);
      setOpen(true);
      // Iter75 Phase 5 — physical confetti cannons in addition to the
      // CSS rain inside the modal. The combination makes a level-up
      // feel like a genuine event, not a popup.
      fireConfettiCannons();
      trackEvent("level_up", { level: d.level, prev: d.previous_level, title: d.title });
    };
    window.addEventListener("oak:level-up", onLevelUp);
    return () => window.removeEventListener("oak:level-up", onLevelUp);
  }, []);

  if (!info) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="border-0 max-w-md w-[calc(100vw-1.5rem)] p-0 overflow-hidden bg-[#0B0E14]"
        data-testid="level-up-modal"
      >
        {/* Confetti background — pure CSS, no library dep. */}
        <div className="relative">
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
            {[...Array(18)].map((_, i) => (
              <span
                key={i}
                className="absolute block rounded-sm"
                style={{
                  left: `${(i * 53) % 100}%`,
                  top: `-10%`,
                  width: 6 + (i % 3) * 2,
                  height: 6 + (i % 3) * 2,
                  background: ["#00FF9D", "#0044FF", "#FFD700", "#FF7B00", "#9b59ff"][i % 5],
                  animation: `oak-confetti ${1.6 + (i % 4) * 0.35}s ${(i % 7) * 0.1}s ease-out forwards`,
                }}
              />
            ))}
          </div>

          {/* Foreground */}
          <div className="relative px-6 pt-8 pb-6 text-center">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-white/40 hover:text-white"
              aria-label="Close"
              data-testid="level-up-close"
            >
              <X size={16} />
            </button>

            {/* Hero level badge */}
            <div className="relative mx-auto h-24 w-24 mb-4" data-testid="level-up-hero">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#00FF9D]/40 to-[#0044FF]/40 blur-2xl" />
              <div className="relative h-24 w-24 rounded-full grid place-items-center bg-gradient-to-br from-[#00FF9D]/22 to-[#0044FF]/22 border-2 border-white/15 shadow-[0_0_60px_-10px_rgba(0,255,157,0.6)]">
                <div className="font-display text-4xl font-bold tabular-nums tracking-tight bg-clip-text text-transparent bg-gradient-to-br from-[#00FF9D] to-[#9bc7ff]">
                  {info.level}
                </div>
              </div>
              <div className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-[#0044FF] text-white grid place-items-center shadow-lg animate-bounce">
                <ChevronUp size={14} />
              </div>
            </div>

            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-mono-oak text-[#00FF9D] mb-1">
              <Sparkles size={10} /> Level up
            </div>
            <DialogTitle className="font-display text-3xl tracking-tight" data-testid="level-up-title">
              You're {info.title}
            </DialogTitle>
            <DialogDescription className="text-white/55 text-sm mt-1.5">
              {info.previous_level != null ? (
                <>Level {info.previous_level} → <span className="text-white font-semibold">Level {info.level}</span>. Keep the streak alive — bigger titles unlock at every milestone.</>
              ) : (
                <>You leveled up to {info.title}. Keep going — every lesson counts toward the next title.</>
              )}
            </DialogDescription>

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-6 w-full py-3 rounded-xl btn-primary font-display font-semibold text-sm"
              data-testid="level-up-continue"
            >
              Keep learning
            </button>
          </div>
        </div>

        {/* CSS keyframes scoped to this component. */}
        <style>{`
          @keyframes oak-confetti {
            0%   { transform: translate3d(0, 0, 0) rotate(0deg);   opacity: 0; }
            10%  { opacity: 1; }
            100% { transform: translate3d(${0}, 160%, 0) rotate(540deg); opacity: 0; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
